import React from 'react';

import * as Sentry from '@sentry/browser';

import { WeezHeader, FrameCard, Button, SplitContainer, AnchorNav, ToggleContainer, Section, TooltipHelp, Icon, ToastContext } from '@weezevent/nacre';

import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useAppContext from '../../../../context/useAppContext';
import { requiredField, useForm } from '../../../../utils/validators';

import QueueSettingsCTA from '../queue-settings-cta';

import { Input } from '../inputs/inputs';
import { DefaultLanguageSelector } from '../language-selector/language-selector';
import { aliases } from '../../../../i18n/i18n';

import { EventSelector } from '../event-selector/event-selector';
import { AdminStatus, WidgetPreview } from './new-widget';
import RateSelector from '../../rateSelector';
import RateModel from '../../../../models/rateModel';
import RateGroup from '../../../../models/rateGroup';
import { InventorySelector } from '../inventory-selector/inventory-selector';
import { ContingentSelector } from '../contingent-selector/contingent-selector';
import { SeatingModeSelector } from '../seating-selector/seating-mode-selector';

import WidgetEvent from '../../../../models/widgetEvent';
import Event from '../../../../models/eventModel';
import Inventory from '../../../../models/inventory';
import Contingent from '../../../../models/contingentModel';
import UserRightsRender from '../../../events/dashboard/components/userRightsRender';
import Widget from '../../../../models/widget';
import { usePermissions } from '../../../../user/permissions';
import PreviewCode from '../preview-code/preview-code';
import { generateCode } from '../../widgetCard';

import Style from './filtered-widget.module.css';

function CreateMatchingNeoWidgetButton({ widgetEvent, widget }) {
    const { t } = useTranslation();
    const history = useHistory();
    const [{ organization }] = useAppContext();
    const [generating, setGenerating] = React.useState(false);

    const redirect = React.useCallback(
        widget_id => {
            window.open(`${window.location.origin}/ticket/O${organization.id}/widgets/${widget_id}/steps`);
        },
        [history, organization]
    );

    const generate_neo_widget = React.useCallback(() => {
        // Only send generate_neo_widget to server
        let widgetEventObject = new WidgetEvent({
            id: widgetEvent.id,
            generate_neo_widget: true
        });

        setGenerating(true);
        return widgetEventObject
            .save({
                organizationId: organization.id
            })
            .then(() => {
                // Redirect ?
                return Widget.findOne(
                    {
                        organizationId: organization.id
                    },
                    {
                        widget_key: widgetEvent.widget_event_key
                    }
                );
            })
            .catch(e => {
                // toast
            })
            .finally(() => {
                setGenerating(false);
            });
    }, [widgetEvent, organization]);

    const generate_or_redirect = React.useCallback(() => {
        if (widget) {
            return redirect(widget.id);
        }

        generate_neo_widget()
            .then(widget => {
                redirect(widget.id);
            })
            .catch(e => {
                // toast
            });
    }, [widget, redirect, generate_neo_widget]);

    if (!widgetEvent) {
        return null;
    }

    return <Button primary label={t('ticket.widget.form.generate_neo_widget')} onClick={generate_or_redirect} loading={generating} />;
}

function WidgetConfigCard({ onChange = () => {}, form, setForm = () => {}, errors, widget = null }) {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();
    const [ratesAndGroups, setRatesAndGroups] = React.useState([]);

    // TODO : create a real new rate selector to replace this crappy thing.
    React.useEffect(() => {
        if (!form.event) {
            return;
        }

        let rates = RateModel.getAll({
            organizationId: organization.id,
            eventId: form.event.id,
            query: {
                limit: 800
            }
        });

        let rateGroups = RateGroup.getAll({ organizationId: organization.id, eventId: form.event.id });

        Promise.all([rates, rateGroups]).then(([rates, rateGroups]) => {
            let ratesWithoutGroup = [];

            for (let rate of rates) {
                if (form.rates.length !== 0) {
                    let rateIsChecked = form.rates.findIndex(id => id === rate.id);
                    if (rateIsChecked >= 0) {
                        rate.isChecked = true;
                    }
                }

                if (rate.group) {
                    let findGroup = rateGroups.find(group => group.id === rate.group);
                    findGroup.rates = findGroup.rates || [];
                    findGroup.rates.push(rate);
                }

                if (!rate.group) {
                    ratesWithoutGroup.push(rate);
                }
            }
            setRatesAndGroups({ rates: ratesWithoutGroup, groups: rateGroups });
        });
    }, [form.event, form.rates, organization]);

    const defaultQueryParams = {
        publication_status__in: 'draft,published'
    };

    const [events, loadingEvents] = Event.useApiModel(
        {
            organizationId: organization.id,
            query: defaultQueryParams
        },
        [organization]
    );

    let all_events = React.useMemo(() => {
        if (!widget | (!events & loadingEvents)) {
            return events;
        }

        let all_events = events;

        let event_linked = events.find(event => event.id === widget.event.id);
        if (!event_linked) {
            let event = new Event(widget.event);
            return all_events.push(event);
        }

        return all_events;
    }, [events, loadingEvents, widget]);

    return (
        <FrameCard id="configuration" title={t('ticket.widgets.common.module_configuration')}>
            <Input
                label={
                    <>
                        <span>{t('ticket.widgets.event.config.name_label')}</span>{' '}
                        <TooltipHelp
                            title={
                                <div>
                                    <Trans i18nKey="ticket.widgets.filtered_widget.filtered_widget_name_label_tooltip" />
                                </div>
                            }
                        />
                    </>
                }
                placeholder={t('ticket.widgets.event.config.name_placeholder')}
                value={form.name}
                onChange={({ target: { value } }) => onChange('name', value)}
                error={errors['name'] ? [t(errors['name'])] : null}
            />

            <EventSelector
                onChange={e => {
                    onChange(
                        'event',
                        all_events.find(event => event.id === e)
                    );
                    onChange('rates', []);
                }}
                events={all_events}
                loadingEvents={loadingEvents}
                event={form.event}
                error={errors['event'] ? [t(errors['event'])] : null}
                disabled={Boolean(widget && form.is_default)}
            />

            <RateSelector
                ratesAndGroups={ratesAndGroups}
                rateClicked={form.rates}
                setRateClicked={React.useCallback(value => {
                    onChange('rates', value);
                })}
                groupClicked={form.groups}
                setGroupClicked={React.useCallback(value => {
                    onChange('groups', value);
                })}
                form={form}
                setForm={setForm}
            />

            <InventorySelector
                onChange={object => {
                    onChange('inventories', object);
                }}
                eventsSelectedObjects={form.event}
                inventories={form.inventories}
                many={true}
            />
            <ContingentSelector
                onChange={object => {
                    onChange('contingents', object);
                }}
                eventsSelectedObjects={form.event}
                contingents={form.contingents}
                many={true}
            />
            <SeatingModeSelector selectedEvent={form.event} onChange={value => onChange('seating_mode', value)} value={form.seating_mode} />

            <DefaultLanguageSelector language={form.default_language} onChange={onChange} />
            <ToggleContainer
                toggle={form.is_default}
                onToggle={checked => onChange('is_default', checked)}
                title={t('ticket.widget.singleEvent.isDefaultTitle')}
                subtitle={t('ticket.widget.singleEvent.isDefaultSubtitle')}
            />
        </FrameCard>
    );
}

function WidgetCodeCard({ form }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const integration_code = React.useMemo(() => {
        const widget = new WidgetEvent(form);
        if (!widget.pk || !widget.event) {
            return null;
        }
        return generateCode(widget, form.default_language);
    }, [form]);

    if (!integration_code) {
        return null;
    }

    return (
        <FrameCard id="code" title={t('ticket.widgets.common.module_code')} subtitle={t('ticket.widgets.common.module_code_sublabel')}>
            <PreviewCode value={integration_code} />
            <Button
                primary
                onClick={() => {
                    navigator.clipboard.writeText(integration_code);
                    toast.success(t('ticket.common.cta.copied_clipboard'));
                }}
                icon={<Icon name={'copy'} margin={false} />}
                fluid={true}
                style={{ marginBottom: '20px' }}
                label={t('ticket.common.cta.copy')}
            />
            <Section>
                <div>
                    <Trans i18nKey="ticket.widgets.common.module_code_help" />
                    <a href="https://support.weezevent.com/fr/integrer-billetterie-site-web" target="_blank" rel="noopener noreferrer">
                        {' '}
                        {t('ticket.widgets.common.module_code_help_url')}{' '}
                    </a>
                </div>
            </Section>
        </FrameCard>
    );
}

function WidgetQueueCard({ form, widget, matchingWidget }) {
    const { t } = useTranslation();
    return (
        <FrameCard id="queue" title={t('ticket.widgets.common.module_queue')} subtitle={t('ticket.widgets.common.module_queue_placeholder')}>
            <QueueSettingsCTA disabled={!form.id} eventId={form.event ? form.event.id : null} widgetKey={form.widget_event_key}>
                <CreateMatchingNeoWidgetButton widgetEvent={widget} widget={matchingWidget} />
            </QueueSettingsCTA>
        </FrameCard>
    );
}

export default function NewFilteredWidget({ widget = null, matchingWidget = null, sync = () => {} }) {
    const { t } = useTranslation();
    const history = useHistory();
    const permissions = usePermissions();

    const [{ organization }] = useAppContext();
    const toast = React.useContext(ToastContext);
    const locale = organization.defaultLanguage();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [iframeKey, setIframeKey] = React.useState(Math.random().toString(16));

    const [form, setValue, setForm, errors, validate] = useForm(
        widget
            ? {
                  ...widget.toAPI(),
                  event: new Event(widget.event),
                  groups: [], // not in API
                  inventories: widget.inventories.map(invt => new Inventory(invt)),
                  contingents: widget.contingents.map(cont => new Contingent(cont)),
                  default_language: aliases[widget.default_language] ?? widget.default_language
              }
            : {
                  name: '',
                  event: null,
                  all_inventories: false,
                  inventories: [],
                  all_contingents: false,
                  contingents: [],
                  all_rates: false,
                  rates: [],
                  groups: [], // not in API
                  default_language: locale,
                  access_code: null,
                  is_default: false,
                  seating_mode: 'automatic'
              },
        {
            name: requiredField,
            event: requiredField
        }
    );

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        const newWidget = new WidgetEvent(form);
        if (!validate()) {
            toast.error(t('ticket.widgets.event.errors'));
            setIsSubmitting(false);
            return;
        }
        newWidget.events = newWidget.event.pk;
        newWidget.inventories = newWidget.inventories.map(e => e.pk);
        newWidget.contingents = newWidget.contingents.map(e => e.pk);

        if (widget) {
            newWidget.widget_event_key = widget.widget_event_key;
            newWidget.id = widget.id;
        }

        if (newWidget.rates.length === 0) {
            toast.error(t('ticket.widgets.filtered_widget.error_rates'));
            setIsSubmitting(false);
            return;
        }

        newWidget
            .save({ organizationId: organization.pk })
            .then(({ response }) => {
                toast.success(t(`ticket.widget.form.${widget ? 'successSave' : 'successCreate'}`));
                if (!widget) {
                    history.push(`/ticket/O${organization.pk}/widgets/filtered/${response.id}`);
                }
                sync();
                setIframeKey(Math.random().toString(16));
            })
            .catch(e => {
                console.error(e);
                Sentry.captureException(e);
                toast.error(t('ticket.widget.form.failCreate'));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [form, organization, toast, history, widget, sync]);

    const navBarItems = React.useMemo(() => {
        // Hopefully someday items will be an array of jsx elements
        const items = [
            {
                id: 'configuration',
                title: t('ticket.widgets.event.anchor.config.anchor')
            },
            {
                id: 'code',
                title: t('ticket.widgets.event.anchor.code.anchor')
            }
        ];

        if (permissions.hasRole('admin')) {
            items.push({
                id: 'queue',
                title: (
                    <>
                        {t('ticket.widgets.event.anchor.queue.anchor')} <AdminStatus />
                    </>
                )
            });
        }

        return items;
    }, [permissions]);

    return (
        <div className={Style['filtered-widget__container']}>
            <WeezHeader
                title={t('ticket.widgets.filtered_widget.title')}
                backLink={{
                    onClick: () => history.push(`/ticket/O${organization.pk}/widgets`)
                }}
                rightComponent={[
                    <Button primary key="top-cta-widget" label={t('ticket.common.action.save')} onClick={handleSubmit} isLoading={isSubmitting} disabled={isSubmitting} />
                ]}
            />

            <AnchorNav items={navBarItems} rootId="main">
                <SplitContainer size="maxi" additional={<WidgetPreview iframeKey={iframeKey} widget={widget} />}>
                    <WidgetConfigCard setIframeKey={setIframeKey} onChange={setValue} form={form} setForm={setForm} errors={errors} widget={widget} />
                    <WidgetCodeCard onChange={setValue} form={form} />
                    <UserRightsRender>
                        <WidgetQueueCard form={form} widget={widget} matchingWidget={matchingWidget} />
                    </UserRightsRender>
                </SplitContainer>
            </AnchorNav>
        </div>
    );
}
